/**
 * Composable for handling file drops in Tauri applications
 * 
 * In Tauri, external file drops (from OS file system) don't work with HTML5 drag-and-drop.
 * We need to use Tauri's event system instead.
 */

import { onMounted, onUnmounted, ref } from 'vue'
import { Platform } from '@/lib/platform'
import { logger } from '@/lib/logger'

export interface FileDropHandler {
  (files: File[]): void | Promise<void>
}

export interface TauriFileDropOptions {
  /** Only accept files matching these extensions (e.g., ['.png', '.jpg']) */
  acceptedExtensions?: string[]
  /** Only accept files matching these MIME types (e.g., ['image/*']) */
  acceptedTypes?: string[]
  /** Enable logging for debugging */
  debug?: boolean
}

export function useTauriFileDrop(
  handler: FileDropHandler,
  options: TauriFileDropOptions = {}
) {
  const isDragging = ref(false)
  let unlisten: (() => void) | null = null

  const filterFiles = (files: File[]): File[] => {
    let filtered = files

    if (options.acceptedTypes?.length) {
      filtered = filtered.filter(file => {
        return options.acceptedTypes!.some(type => {
          if (type.endsWith('/*')) {
            const prefix = type.slice(0, -2)
            return file.type.startsWith(prefix)
          }
          return file.type === type
        })
      })
    }

    if (options.acceptedExtensions?.length) {
      filtered = filtered.filter(file => {
        const ext = '.' + file.name.split('.').pop()?.toLowerCase()
        return options.acceptedExtensions!.some(accepted => 
          ext === accepted.toLowerCase()
        )
      })
    }

    return filtered
  }

  const setupTauriListener = async () => {
    if (!Platform.isDesktop()) return

    try {
      const { getCurrentWindow } = await import('@tauri-apps/api/window')
      const { listen } = await import('@tauri-apps/api/event')
      
      const currentWindow = getCurrentWindow()
      
      if (options.debug) {
        logger.info('[TauriFileDrop] Setting up file drop listeners')
      }

      // Track if we're currently in an internal drag (from app, not from OS)
      let isInternalDrag = false
      let internalDragEndTimeout: ReturnType<typeof setTimeout> | null = null
      
      // Listen for HTML5 dragstart to detect internal drags
      const handleInternalDragStart = () => {
        // Clear any pending timeout from previous drag
        if (internalDragEndTimeout) {
          clearTimeout(internalDragEndTimeout)
          internalDragEndTimeout = null
        }
        isInternalDrag = true
        if (options.debug) {
          logger.debug('[TauriFileDrop] Internal drag started')
        }
      }
      
      const handleInternalDragEnd = () => {
        // Delay resetting the flag to allow Tauri's drop event to fire first
        // Tauri events are async and may fire after HTML5 dragend
        internalDragEndTimeout = setTimeout(() => {
          isInternalDrag = false
          internalDragEndTimeout = null
          if (options.debug) {
            logger.debug('[TauriFileDrop] Internal drag ended (delayed reset)')
          }
        }, 100)
      }
      
      // Prevent Tauri from intercepting internal drags by calling preventDefault
      // on dragover/dragenter at the window level during internal drags.
      // This allows HTML5 drag-and-drop to work normally for internal operations.
      // See: https://github.com/tauri-apps/tauri/issues/3277
      const handleWindowDragOver = (e: DragEvent) => {
        if (isInternalDrag) {
          e.preventDefault()
          e.stopPropagation()
        }
      }
      
      const handleWindowDragEnter = (e: DragEvent) => {
        if (isInternalDrag) {
          e.preventDefault()
          e.stopPropagation()
        }
      }
      
      // Add global listeners for HTML5 drag events
      document.addEventListener('dragstart', handleInternalDragStart)
      document.addEventListener('dragend', handleInternalDragEnd)
      // Add window-level listeners to prevent Tauri from intercepting internal drags
      window.addEventListener('dragover', handleWindowDragOver, { capture: true })
      window.addEventListener('dragenter', handleWindowDragEnter, { capture: true })

      // Listen for drag hover (tauri://drag-over in v2, tauri://drag in v1)
      const unlistenHover = await listen('tauri://drag-over', () => {
        // Only set dragging if this is NOT an internal drag
        if (!isInternalDrag) {
          isDragging.value = true
          if (options.debug) {
            logger.debug('[TauriFileDrop] External drag hover detected')
          }
        }
      })
      
      // Also listen for the old event name for compatibility
      const unlistenHoverLegacy = await listen('tauri://drag', () => {
        if (!isInternalDrag) {
          isDragging.value = true
        }
      })

      // Listen for drag leave (tauri://drag-leave in v2)
      const unlistenLeave = await listen('tauri://drag-leave', () => {
        isDragging.value = false
        if (options.debug) {
          logger.debug('[TauriFileDrop] Drag leave')
        }
      })
      
      // Also listen for old cancelled event
      const unlistenCancelled = await listen('tauri://drag-cancelled', () => {
        isDragging.value = false
      })

      // Listen for file drop
      const unlistenDrop = await listen<{ paths?: string[], position?: { x: number, y: number } }>('tauri://drag-drop', async (event) => {
        isDragging.value = false
        
        // Skip if this is an internal drag (no file paths)
        if (isInternalDrag) {
          if (options.debug) {
            logger.debug('[TauriFileDrop] Ignoring internal drag drop')
          }
          return
        }
        
        // In Tauri v2, the payload structure might be different
        const paths = event.payload?.paths || []
        
        if (options.debug) {
          logger.info('[TauriFileDrop] Files dropped:', paths)
          logger.debug('[TauriFileDrop] Full event payload:', JSON.stringify(event.payload))
        }

        if (paths.length === 0) {
          if (options.debug) {
            logger.warn('[TauriFileDrop] No paths in drop event')
          }
          return
        }

        try {
          const { readFile } = await import('@tauri-apps/plugin-fs')
          
          // Convert file paths to File objects
          const files: File[] = []
          
          for (const path of paths) {
            try {
              // Read file as Uint8Array
              const contents = await readFile(path)
              
              // Get filename from path
              const filename = path.split('/').pop() || path.split('\\').pop() || 'unknown'
              
              // Determine MIME type from extension
              const ext = filename.split('.').pop()?.toLowerCase()
              const mimeType = getMimeTypeFromExtension(ext || '')
              
              // Create File object
              const file = new File([contents], filename, { type: mimeType })
              files.push(file)
              
              if (options.debug) {
                logger.debug(`[TauriFileDrop] Converted: ${filename} (${mimeType})`)
              }
            } catch (error) {
              logger.error(`[TauriFileDrop] Failed to read file: ${path}`, error)
            }
          }

          if (files.length > 0) {
            const filteredFiles = filterFiles(files)
            
            if (options.debug) {
              logger.info(`[TauriFileDrop] Processing ${filteredFiles.length}/${files.length} files`)
            }
            
            if (filteredFiles.length > 0) {
              await handler(filteredFiles)
            }
          }
        } catch (error) {
          logger.error('[TauriFileDrop] Error processing dropped files:', error)
        }
      })

      // Store cleanup function
      unlisten = () => {
        if (internalDragEndTimeout) {
          clearTimeout(internalDragEndTimeout)
          internalDragEndTimeout = null
        }
        document.removeEventListener('dragstart', handleInternalDragStart)
        document.removeEventListener('dragend', handleInternalDragEnd)
        window.removeEventListener('dragover', handleWindowDragOver, { capture: true })
        window.removeEventListener('dragenter', handleWindowDragEnter, { capture: true })
        unlistenHover()
        unlistenHoverLegacy()
        unlistenLeave()
        unlistenCancelled()
        unlistenDrop()
        if (options.debug) {
          logger.info('[TauriFileDrop] Cleaned up listeners')
        }
      }

      if (options.debug) {
        logger.success('[TauriFileDrop] File drop listeners ready')
      }
    } catch (error) {
      logger.error('[TauriFileDrop] Failed to setup Tauri file drop:', error)
    }
  }

  onMounted(() => {
    setupTauriListener()
  })

  onUnmounted(() => {
    if (unlisten) {
      unlisten()
      unlisten = null
    }
  })

  return {
    isDragging
  }
}

/**
 * Get MIME type from file extension
 */
function getMimeTypeFromExtension(ext: string): string {
  const mimeTypes: Record<string, string> = {
    // Images
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
    'bmp': 'image/bmp',
    'ico': 'image/x-icon',
    
    // Videos
    'mp4': 'video/mp4',
    'webm': 'video/webm',
    'ogv': 'video/ogg',
    'mov': 'video/quicktime',
    'avi': 'video/x-msvideo',
    
    // Audio
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'oga': 'audio/ogg',
    
    // Documents
    'pdf': 'application/pdf',
    'json': 'application/json',
    'txt': 'text/plain',
    'html': 'text/html',
    'css': 'text/css',
    'js': 'text/javascript',
  }
  
  return mimeTypes[ext.toLowerCase()] || 'application/octet-stream'
}
